import styled from 'styled-components'

/**
 * Styled Nav that alternates with `StyledNavWithIdSelectorOverrides`
 * Follows dropdown logic of flagship ExpressNavBar
 *
 * @alias SubHeaderNavDropdown
 * @see ExpressNavBar
 * @see StyledNavWithIdSelectorOverrides
 */
export const StyledNavWrapperWithDropDown = styled.nav.attrs(props => ({
  id: props.id,
  className: 'animated fadeIn'
}))`
  --dropdownBackgroundColor: ${props =>
    props.palette('subheader.backgroundColor.md')};
  --navStrokeHeight: ${props => props.palette('tabs.keylineHeight.md')};

  margin-left: var(--navLeftMargin);
  background-color: var(--dropdownBackgroundColor);
  height: var(--navHeight); /* Menu height */
  overflow: hidden; /* Don't show anything outside the nav */

  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
    max-height: calc(var(--navHeight) * 2); /* Menu height x 2 */
    position: relative; /* Position the menu button relative to this item */
  }

  li {
    display: inline-block;
  }

  a {
    cursor: pointer;
    display: inline-block;
    padding: 0 1em; // 0 1em
    color: var(--clr-nav-text);
    text-decoration: none;
    text-transform: ${props => props.palette('tabs.textTransforms.md')};
    white-space: nowrap;
    line-height: calc(var(--navHeight) - 3px); /* Menu height  40*/
    height: var(--navHeight); /* Menu height 42*/
    position: relative;
    border-radius: ${props => props.palette('tabs.borderRadius.md')};
    font-size: ${props => props.palette('tabs.fontSize.md')};
    font-weight: ${props => props.palette('tabs.fontWeight.md')};
    letter-spacing: ${props => props.palette('tabs.letterSpacing.md')};
  }

  /* The keyline underscore pseudo element */
  a.nav-link:after,
  a:after {
    content: '';
    display: block;
    position: absolute;
    height: ${props => props.palette('tabs.keylineHeight.md')};
    width: ${props => props.palette('tabs.keylineWidth.md')};
    background: ${props => props.palette('tabs.keylineBackgroundColor.md')};
    border-radius: ${props => props.palette('tabs.keylineBorderRadius.md')};

    /* top: calc(var(--navHeight) - var(--navStrokeHeight) + 0.1rem); */
    top: ${props => `calc(100% - ${props.palette('tabs.keylineHeight.md')})`};
    left: 10px;
    right: 10px;

    -webkit-transform-origin: left;
    -ms-transform-origin: left;
    transform-origin: left;

    -webkit-transform: scale(0, 1);
    -ms-transform: scale(0, 1);
    transform: scale(0, 1);

    -webkit-transition: -webkit-transform ease-in-out 250ms;
    -webkit-transition: transform ease-in-out 250ms;
    transition: transform ease-in-out 250ms;
  }

  /* Disable keyline pseudo on more/close button */
  .menu-toggle a:after {
    display: none;
  }

  /* Feedback: Animate the keyline pseudo */
  a.nav-link:hover::after,
  a.nav-link:focus::after,
  a:hover::after,
  a:focus::after,
  a.nav-link.active-nav:after {
    transform: scale(1, 1);
  }

  a:hover svg,
  a:focus svg,
  a:active svg,
  a.active-nav svg {
    opacity: 1;
  }

  svg {
    width: ${props => props.palette('tabs.svgSize.md')};
    height: ${props => props.palette('tabs.svgSize.md')};
    fill: var(--white);
    margin: 5px 10px 5px 0px;
    display: inline-block;
    vertical-align: middle;
  }

  /* The menu button */
  .menu-open {
    position: absolute; /* Move the menu button out of flow */
    right: -12px;
    bottom: var(
      --navHeight
    ); /* Move upwards, the same distance as the menu height */
    background-image: linear-gradient(
      to right,
      rgba(19, 51, 61, 0) 0,
      var(--dropdownBackgroundColor) 2em
    );
    padding-left: 3em;
  }

  /* The close button */
  .menu-close {
    display: none;
  }

  /* Feedback: The close and more button  */
  .menu-toggle a:hover,
  .menu-toggle a:focus,
  .menu-toggle a:active,
  .menu-toggle a.active-nav {
    background-color: rgba(255, 255, 255, 0.25);
  }

  /* Expanded mode: Radius on, box-shadow on */
  &.expanded {
    height: auto;
    padding: 0;
    position: absolute;
    right: 0;
    left: 0;
    z-index: 10;
    border-radius: var(--border-radius);
    box-shadow: 0 1px 2px rgba(19, 51, 61, 0.5);
  }

  &.expanded ul {
    max-height: none;
  }

  &.expanded li {
    display: block;
  }

  &.expanded a {
    display: block;
  }

  &.expanded a:hover,
  &.expanded a.nav-link.active-nav,
  a:focus {
    // Alternative to linear gradient behind More
    /* background-color: rgba(255, 255, 255, 0.25); */
  }

  &.expanded a.nav-link:after,
  &.expanded a:after {
    width: 200px;
    left: 10px;
    right: 0;
  }

  &.expanded .menu-toggle a:after {
    display: none;
  }

  &.expanded li:not(:first-child) {
    margin-top: 2px;
  }

  &.expanded li:last-child {
    display: none;
  }

  &.expanded li.menu-close {
    display: inline-block;
    position: absolute;
    top: 0;
    right: 0;
    margin: 0;
    border-left: 2px solid var(--dropdownBackgroundColor);
  }
  &.expanded li.menu-toggle span:first-child {
    margin-bottom: 0;
  }
  .menu-toggle {
    a {
      background-color: var(--dropdownBackgroundColor);
      display: flex;
      align-items: center;
      border-radius: var(--button-border-radius);
    }

    /* More and Close button icons feedback */
    a span:first-child {
      opacity: 0.5;
    }
    a:hover span:first-child {
      opacity: 1;
    }
    /* spacing for More icon only */
    span:first-child {
      margin-bottom: 6px;
    }
    span {
      font-size: var(--small-icon-size);
      margin-right: 5px;
    }
    span + span {
      font-size: unset;
    }
  }

  .menu-toggle {
    padding-right: 0px;
    float: right;
  }

  /** No media queries as this module does not display below 768  */
`
/** Name styled-components so they don't trace in ReactDevTools as anonymous `styled.div`s  */
/** Feel free to change in dev */
StyledNavWrapperWithDropDown.displayName = 'StyledNavWrapperWithDropDown'



// WEBPACK FOOTER //
// ./src/components/SubHeader/styled/StyledNavWrapperWithDropDown.js